import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-jalaali';
import PersianDatePicker from '../components/PersianDatePicker';
import { getApiBaseUrl } from '../utils/getApiUrl';
import { HIS_MENU_TITLE } from '../config/hisSections';
import { canAccessHisReport } from '../utils/hisReportAccess';

import { rialMoneyStat } from '../utils/formatMoneyFa';

type PaymentType = 'cash' | 'insurance' | 'all';
type DatePreset = 'today' | 'month' | 'last7' | 'custom';

interface HisOperator {
  user_id: number;
  username: string;
  full_name: string;
}

interface HisMachine {
  machine_id: number;
  machine_name: string;
  display_name: string;
}

interface OperatorReportItem {
  user_ref: number;
  operator_name: string;
  username: string;
  reception_count: number;
  admitted_timing_count: number;
  timing_count: number;
  cash_count: number;
  dowork_count: number;
  cash_amount: number;
  insurance_amount: number;
  total_amount: number;
}

interface OperatorReport {
  from_date: string;
  to_date: string;
  user_ref: number | null;
  machine_ref: number | null;
  machine_label: string | null;
  payment_type: PaymentType;
  payment_type_label: string;
  operator_count: number;
  total_receptions: number;
  total_admitted_timings: number;
  total_timings: number;
  total_cash_swipes: number;
  total_doworks: number;
  total_cash: number;
  total_insurance: number;
  total_amount: number;
  items: OperatorReportItem[];
}

interface ConnectionStatus {
  configured: boolean;
  ok?: boolean;
}

const defaultFromDate = (): string =>
  moment().startOf('jMonth').format('jYYYY/jMM/jDD');

const defaultToDate = (): string =>
  moment().format('jYYYY/jMM/jDD');

const PAYMENT_OPTIONS: { id: PaymentType; label: string; desc: string }[] = [
  { id: 'all', label: 'همه', desc: 'نقدی + بیمه' },
  { id: 'cash', label: 'نقدی', desc: 'فقط سهم بیمار' },
  { id: 'insurance', label: 'بیمه', desc: 'فقط سهم بیمه' },
];

const DATE_PRESETS: { id: DatePreset; label: string; apply: () => { from: string; to: string } }[] = [
  {
    id: 'today',
    label: 'امروز',
    apply: () => {
      const d = moment().format('jYYYY/jMM/jDD');
      return { from: d, to: d };
    },
  },
  {
    id: 'month',
    label: 'ماه جاری',
    apply: () => ({
      from: moment().startOf('jMonth').format('jYYYY/jMM/jDD'),
      to: moment().format('jYYYY/jMM/jDD'),
    }),
  },
  {
    id: 'last7',
    label: '۷ روز اخیر',
    apply: () => ({
      from: moment().subtract(6, 'day').format('jYYYY/jMM/jDD'),
      to: moment().format('jYYYY/jMM/jDD'),
    }),
  },
];

const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
  if (rank === 1) {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700 ring-2 ring-amber-200">
        ۱
      </span>
    );
  }
  if (rank === 2) {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-600 ring-2 ring-slate-300">
        ۲
      </span>
    );
  }
  if (rank === 3) {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-orange-100 text-xs font-bold text-orange-700 ring-2 ring-orange-200">
        ۳
      </span>
    );
  }
  return (
    <span className="inline-flex h-7 w-7 items-center justify-center text-sm font-medium text-slate-400">
      {rank.toLocaleString('fa-IR')}
    </span>
  );
};

type DetailKind = 'reception' | 'timing' | 'cash' | 'dowork' | 'dowork_from_timing';

interface DetailListItem {
  record_id: number | null;
  patient_name: string;
  event_date: string;
  record_code?: string;
  operator_name: string;
  username: string;
  timing_date?: string;
  service_name?: string;
  cash_amount?: number;
  insurance_amount?: number;
  amount?: number;
  has_card_payment?: boolean;
  has_dowork?: boolean;
  insurance_name?: string;
}

interface DetailListResponse {
  kind: DetailKind;
  kind_label: string;
  from_date: string;
  to_date: string;
  user_ref: number | null;
  count: number;
  items: DetailListItem[];
}

const CARD_TIPS = {
  reception:
    'تعداد نوبت‌هایی که این کاربر ثبت کرده و در بازه انتخاب‌شده پذیرش هم شده‌اند. برای دیدن لیست کلیک کنید.',
  receptionService:
    'تعداد خدمات ثبت‌شده برای پذیرش‌های منتج از نوبت‌دهی این کاربر در بازه، با فیلتر خدمت انتخاب‌شده. برای دیدن لیست کلیک کنید.',
  timing:
    'تعداد وقت‌دهی ثبت‌شده توسط این کاربر (مطابق آمار وقت‌های HIS). برای دیدن لیست کلیک کنید.',
  cashSwipe:
    'تعداد بیماران پذیرش‌شده توسط این کاربر که در همین بازه کارت‌کشی/صندوق هم داشته‌اند. برای دیدن لیست کلیک کنید.',
  dowork:
    'تعداد بیمارانِ پذیرش‌شده از لیست نوبت‌دهی این کاربر که در همین بازه انجام‌کار برایشان ثبت شده. برای دیدن لیست کلیک کنید.',
  cashAmount:
    'جمع دریافتی صندوق این کاربر در بازه. جمع همهٔ کاربران برابر گزارش صندوق HIS است (تاریخ صندوق).',
  insuranceAmount: 'جمع سهم بیمه آخرین سند پذیرش‌هایی که این کاربر در همین بازه ثبت کرده است.',
  totalAmount: 'جمع دریافتی صندوق و سهم بیمه.',
};

const StatCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  tip?: string;
  accent: 'indigo' | 'emerald' | 'sky' | 'violet' | 'amber' | 'rose' | 'teal';
  icon: React.ReactNode;
  onClick?: () => void;
}> = ({ label, value, sub, tip, accent, icon, onClick }) => {
  const accents = {
    indigo: 'from-indigo-500/10 to-indigo-600/5 text-indigo-600 border-indigo-100',
    emerald: 'from-emerald-500/10 to-emerald-600/5 text-emerald-600 border-emerald-100',
    sky: 'from-sky-500/10 to-sky-600/5 text-sky-600 border-sky-100',
    violet: 'from-violet-500/10 to-violet-600/5 text-violet-600 border-violet-100',
    amber: 'from-amber-500/10 to-amber-600/5 text-amber-600 border-amber-100',
    rose: 'from-rose-500/10 to-rose-600/5 text-rose-600 border-rose-100',
    teal: 'from-teal-500/10 to-teal-600/5 text-teal-600 border-teal-100',
  };

  const clickable = Boolean(onClick);

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={`group/card relative rounded-2xl border bg-gradient-to-br p-4 shadow-sm transition duration-200 ease-out hover:z-20 hover:-translate-y-1 hover:scale-[1.03] hover:shadow-xl ${accents[accent]} ${
        clickable ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400/40' : ''
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <p className="text-xs font-medium text-slate-500">{label}</p>
          {tip ? (
            <span className="group/tip relative inline-flex shrink-0">
              <button
                type="button"
                className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-200/80 text-[10px] font-bold text-slate-600 hover:bg-slate-300"
                aria-label={tip}
                onClick={(e) => e.stopPropagation()}
              >
                ؟
              </button>
              <span
                role="tooltip"
                className="pointer-events-none absolute right-0 top-full z-50 mt-2 hidden w-52 rounded-lg bg-slate-800 px-3 py-2 text-[11px] font-normal leading-relaxed text-white shadow-lg group-hover/tip:block"
              >
                {tip}
              </span>
            </span>
          ) : null}
        </div>
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/90 shadow-sm">
          {icon}
        </div>
      </div>
      <p
        dir="ltr"
        className="mt-3 text-right text-base font-bold tabular-nums tracking-tight text-slate-900 sm:text-lg"
      >
        <span className="whitespace-nowrap">{value}</span>
        {sub ? (
          <span className="mr-1.5 whitespace-nowrap text-xs font-semibold text-slate-500">
            {sub}
          </span>
        ) : null}
      </p>
      {clickable ? (
        <p className="mt-2 text-[10px] font-medium text-slate-400 opacity-0 transition-opacity group-hover/card:opacity-100">
          برای مشاهده لیست کلیک کنید
        </p>
      ) : null}
    </div>
  );
};

const ICONS = {
  clipboard: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  ),
  currency: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  calendar: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  card: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  ),
  check: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

const UserStatBlock: React.FC<{
  rank: number;
  item: OperatorReportItem;
  paymentType: PaymentType;
  countLabel?: string;
  receptionTip?: string;
  onOpenDetail: (kind: DetailKind, userRef: number) => void;
}> = ({ rank, item, paymentType, onOpenDetail }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-3">
      <RankBadge rank={rank} />
      <div className="min-w-0">
        <p className="truncate text-sm font-bold text-slate-900">{item.operator_name || '—'}</p>
        <p className="font-mono text-xs text-slate-500">{item.username}</p>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      <StatCard
        label="تعداد پذیرش"
        value={(item.admitted_timing_count ?? 0).toLocaleString('fa-IR')}
        tip={CARD_TIPS.reception}
        accent="sky"
        icon={ICONS.clipboard}
        onClick={() => onOpenDetail('reception', item.user_ref)}
      />
      <StatCard
        label="نوبت‌دهی"
        value={(item.timing_count ?? 0).toLocaleString('fa-IR')}
        tip={CARD_TIPS.timing}
        accent="amber"
        icon={ICONS.calendar}
        onClick={() => onOpenDetail('timing', item.user_ref)}
      />
      <StatCard
        label="انجام کار"
        value={(item.dowork_count ?? 0).toLocaleString('fa-IR')}
        tip={CARD_TIPS.dowork}
        accent="teal"
        icon={ICONS.check}
        onClick={() => onOpenDetail('dowork_from_timing', item.user_ref)}
      />
    </div>
    <div
      className={`grid gap-3 ${
        paymentType === 'all'
          ? 'grid-cols-1 sm:grid-cols-3'
          : 'grid-cols-1 sm:grid-cols-2'
      }`}
    >
      {paymentType !== 'insurance' && (
        <StatCard
          label="نقدی"
          {...rialMoneyStat(item.cash_amount)}
          tip={CARD_TIPS.cashAmount}
          accent="emerald"
          icon={ICONS.currency}
        />
      )}
      {paymentType !== 'cash' && (
        <StatCard
          label="بیمه"
          {...rialMoneyStat(item.insurance_amount)}
          tip={CARD_TIPS.insuranceAmount}
          accent="violet"
          icon={ICONS.currency}
        />
      )}
      {paymentType === 'all' && (
        <StatCard
          label="جمع"
          {...rialMoneyStat(item.total_amount)}
          tip={CARD_TIPS.totalAmount}
          accent="indigo"
          icon={ICONS.currency}
        />
      )}
    </div>
  </div>
);

const DetailListModal: React.FC<{
  open: boolean;
  loading: boolean;
  error: string | null;
  data: DetailListResponse | null;
  operatorLabel?: string;
  onClose: () => void;
}> = ({ open, loading, error, data, operatorLabel, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const showTimingDate = data?.kind === 'timing';
  const showAmount = data?.kind === 'cash' || data?.kind === 'dowork' || data?.kind === 'dowork_from_timing';
  const amountLabel = data?.kind === 'cash' ? 'مبلغ دریافتی' : 'مبلغ';
  const showMoneySplit = data?.kind === 'dowork' || data?.kind === 'dowork_from_timing';
  const showInsuranceName = data?.kind === 'dowork' || data?.kind === 'dowork_from_timing';

  const formatAmount = (value?: number) =>
    (value ?? 0).toLocaleString('fa-IR');

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-stretch justify-center bg-slate-900/45 p-2 backdrop-blur-sm sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={data?.kind_label || 'جزئیات'}
      onClick={onClose}
    >
      <div
        className="flex h-full w-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-100 bg-gradient-to-l from-indigo-50 via-white to-white px-4 py-3 sm:px-6 sm:py-4">
          <div className="min-w-0">
            <p className="text-xs font-medium text-indigo-600">جزئیات گزارش HIS</p>
            <h2 className="mt-0.5 text-lg font-bold text-slate-900 sm:text-xl">
              {data?.kind_label || 'در حال بارگذاری…'}
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              {[
                data ? `${data.from_date} تا ${data.to_date}` : null,
                operatorLabel || null,
                data ? `${data.count.toLocaleString('fa-IR')} مورد` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            {data?.kind === 'dowork' || data?.kind === 'dowork_from_timing' ? (
              <p className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] text-slate-500">
                <svg className="h-3.5 w-3.5 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                علامت کارت یعنی برای این بیمار مبلغ دریافتی در صندوق ثبت شده است
              </p>
            ) : null}
            {data?.kind === 'cash' ? (
              <p className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] text-slate-500">
                <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-100 text-[10px] font-bold text-amber-700">
                  !
                </span>
                علامت تعجب یعنی هنوز انجام‌کار برای این بیمار ثبت نشده است
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-800"
            aria-label="بستن"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-2 py-3 sm:px-4">
          {loading ? (
            <div className="flex h-48 items-center justify-center text-sm text-slate-500">
              در حال دریافت لیست…
            </div>
          ) : error ? (
            <div className="m-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
              {error}
            </div>
          ) : !data?.items.length ? (
            <div className="flex h-48 items-center justify-center text-sm text-slate-500">
              موردی یافت نشد
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="sticky top-0 z-10 bg-slate-50 text-xs font-semibold text-slate-600">
                  <tr>
                    <th className="whitespace-nowrap px-3 py-2.5 text-right">#</th>
                    <th className="whitespace-nowrap px-3 py-2.5 text-right">نام بیمار</th>
                    <th className="min-w-[10rem] px-3 py-2.5 text-right">نوع خدمت</th>
                    {showInsuranceName ? (
                      <th className="min-w-[8rem] whitespace-nowrap px-3 py-2.5 text-right">بیمه</th>
                    ) : null}
                    <th className="whitespace-nowrap px-3 py-2.5 text-right">تاریخ</th>
                    {showTimingDate ? (
                      <th className="whitespace-nowrap px-3 py-2.5 text-right">تاریخ نوبت</th>
                    ) : null}
                    <th className="whitespace-nowrap px-3 py-2.5 text-right">کد پرونده</th>
                    <th className="whitespace-nowrap px-3 py-2.5 text-right">کاربر</th>
                    {showAmount ? (
                      <th className="whitespace-nowrap px-3 py-2.5 text-right">{amountLabel}</th>
                    ) : null}
                    {showMoneySplit ? (
                      <>
                        <th className="whitespace-nowrap px-3 py-2.5 text-right">نقدی</th>
                        <th className="whitespace-nowrap px-3 py-2.5 text-right">بیمه</th>
                      </>
                    ) : null}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {data.items.map((row, index) => (
                    <tr key={`${row.record_id ?? index}-${row.event_date}-${row.username}-${row.service_name ?? ''}`} className="hover:bg-slate-50/80">
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-400">
                        {(index + 1).toLocaleString('fa-IR')}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-900">
                        <span className="inline-flex items-center gap-1.5">
                          {data.kind === 'cash' && !row.has_dowork ? (
                            <span
                              className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700 ring-1 ring-amber-200"
                              title="انجام‌کار ثبت نشده"
                              aria-label="انجام‌کار ثبت نشده"
                            >
                              !
                            </span>
                          ) : null}
                          {row.patient_name || '—'}
                        </span>
                      </td>
                      <td className="max-w-[18rem] px-3 py-2 text-slate-700">
                        <span className="line-clamp-2" title={row.service_name || undefined}>
                          {row.service_name || '—'}
                        </span>
                      </td>
                      {showInsuranceName ? (
                        <td className="max-w-[12rem] px-3 py-2 text-slate-700">
                          {row.insurance_name ? (
                            <span className="line-clamp-2" title={row.insurance_name}>
                              {row.insurance_name}
                            </span>
                          ) : (
                            <span className="text-slate-400">آزاد</span>
                          )}
                        </td>
                      ) : null}
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-600" dir="ltr">
                        {row.event_date || '—'}
                      </td>
                      {showTimingDate ? (
                        <td className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-600" dir="ltr">
                          {row.timing_date || '—'}
                        </td>
                      ) : null}
                      <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                        {row.record_code || '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                        <span className="font-medium">{row.operator_name || '—'}</span>
                        {row.username ? (
                          <span className="mr-1 font-mono text-xs text-slate-400">· {row.username}</span>
                        ) : null}
                      </td>
                      {showAmount ? (
                        <td className="whitespace-nowrap px-3 py-2 font-semibold tabular-nums text-slate-900" dir="ltr">
                          <span className="inline-flex items-center justify-end gap-1.5">
                            {(data.kind === 'dowork' || data.kind === 'dowork_from_timing') &&
                            row.has_card_payment ? (
                              <span
                                className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-rose-50 text-rose-600 ring-1 ring-rose-100"
                                title="کارت‌کشی / دریافت صندوق انجام شده"
                                aria-label="کارت‌کشی انجام شده"
                              >
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                              </span>
                            ) : null}
                            {formatAmount(row.amount)}
                          </span>
                        </td>
                      ) : null}
                      {showMoneySplit ? (
                        <>
                          <td className="whitespace-nowrap px-3 py-2 tabular-nums text-emerald-700" dir="ltr">
                            {formatAmount(row.cash_amount)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 tabular-nums text-violet-700" dir="ltr">
                            {formatAmount(row.insurance_amount)}
                          </td>
                        </>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const ReportLoadingOverlay: React.FC<{
  open: boolean;
  title: string;
  subtitle: string;
}> = ({ open, title, subtitle }) => {
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/35 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-busy="true"
      aria-label={title}
    >
      <div className="relative w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl shadow-indigo-900/10 ring-1 ring-indigo-100/80">
        <div
          className="h-1 bg-gradient-to-l from-indigo-500 via-violet-500 to-indigo-600"
          aria-hidden
        />
        <div className="pointer-events-none absolute -left-10 -top-10 h-32 w-32 rounded-full bg-indigo-100/70 blur-2xl" aria-hidden />
        <div className="pointer-events-none absolute -bottom-8 -right-8 h-28 w-28 rounded-full bg-violet-100/60 blur-2xl" aria-hidden />

        <div className="relative flex items-center gap-5 px-7 py-6">
          <div className="relative h-14 w-14 shrink-0" aria-hidden>
            <span className="absolute inset-0 animate-spin rounded-full border-[3px] border-indigo-100 border-t-indigo-600" />
            <span className="absolute inset-2 animate-ping rounded-full bg-indigo-400/25" />
            <span className="absolute inset-0 flex items-center justify-center text-indigo-600">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.75}
                  d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </span>
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-gray-900">{title}</p>
            <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
            <span className="mt-3 inline-flex items-center gap-1.5" aria-hidden>
              {[0, 1, 2].map((index) => (
                <span
                  key={index}
                  className="h-1.5 w-1.5 animate-bounce rounded-full bg-indigo-500"
                  style={{ animationDelay: `${index * 140}ms` }}
                />
              ))}
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const AdminHisReports: React.FC = () => {
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(defaultToDate);
  const [datePreset, setDatePreset] = useState<DatePreset>('month');
  const [operators, setOperators] = useState<HisOperator[]>([]);
  const [machines, setMachines] = useState<HisMachine[]>([]);
  const [selectedUserRef, setSelectedUserRef] = useState<string>('');
  const [selectedMachineRef, setSelectedMachineRef] = useState<string>('all');
  const [paymentType, setPaymentType] = useState<PaymentType>('all');
  const [operatorsLoaded, setOperatorsLoaded] = useState(false);
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] = useState<ConnectionStatus | null>(null);
  const [report, setReport] = useState<OperatorReport | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailData, setDetailData] = useState<DetailListResponse | null>(null);
  const [detailOperatorLabel, setDetailOperatorLabel] = useState<string | undefined>();

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    if (!canAccessHisReport()) {
      navigate('/admin/dashboard');
      return;
    }

    const loadStatus = async () => {
      try {
        const apiBaseUrl = getApiBaseUrl();
        const [statusRes, machinesRes] = await Promise.all([
          fetch(`${apiBaseUrl}/admin/reports/his/connection-status`, { headers: authHeaders() }),
          fetch(`${apiBaseUrl}/admin/reports/his/machines`, { headers: authHeaders() }),
        ]);
        if (statusRes.ok) setConnection(await statusRes.json());
        if (machinesRes.ok) {
          const data = await machinesRes.json();
          setMachines(data.items || []);
        }
      } catch {
        /* ignore */
      }
    };
    void loadStatus();
  }, [navigate, authHeaders]);

  const applyDatePreset = (preset: DatePreset) => {
    if (preset === 'custom') {
      setDatePreset('custom');
      return;
    }
    const p = DATE_PRESETS.find((x) => x.id === preset);
    if (!p) return;
    const { from, to } = p.apply();
    setFromDate(from);
    setToDate(to);
    setDatePreset(preset);
    setOperatorsLoaded(false);
    setSelectedUserRef('');
    setReport(null);
  };

  const loadOperators = async () => {
    if (!fromDate || !toDate) {
      setError('لطفاً بازه زمانی را کامل انتخاب کنید');
      return;
    }
    setLoadingOperators(true);
    setError(null);
    setOperatorsLoaded(false);
    setSelectedUserRef('');
    setReport(null);
    try {
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
      const apiBaseUrl = getApiBaseUrl();
      const res = await fetch(
        `${apiBaseUrl}/admin/reports/his/operators-in-range?${params}`,
        { headers: authHeaders() }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'خطا در دریافت لیست کاربران');
      }
      const data = await res.json();
      setOperators(data.items || []);
      setOperatorsLoaded(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'خطای ناشناخته');
    } finally {
      setLoadingOperators(false);
    }
  };

  const fetchReport = async () => {
    if (!fromDate || !toDate) {
      setError('بازه زمانی را انتخاب کنید');
      return;
    }
    if (!selectedUserRef) {
      setError('کاربر را انتخاب کنید');
      return;
    }
    setLoadingReport(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        from_date: fromDate,
        to_date: toDate,
        payment_type: paymentType,
      });
      if (selectedUserRef !== 'all') params.set('user_ref', selectedUserRef);
      if (selectedMachineRef !== 'all') params.set('machine_ref', selectedMachineRef);

      const apiBaseUrl = getApiBaseUrl();
      const res = await fetch(
        `${apiBaseUrl}/admin/reports/his/operator-revenue?${params}`,
        { headers: authHeaders() }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'خطا در دریافت گزارش');
      }
      setReport(await res.json());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'خطای ناشناخته');
    } finally {
      setLoadingReport(false);
    }
  };

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setDetailLoading(false);
    setDetailError(null);
  }, []);

  const openDetail = useCallback(
    async (kind: DetailKind, userRef?: number | null) => {
      if (!report) return;

      const resolvedUserRef =
        userRef != null
          ? userRef
          : selectedUserRef && selectedUserRef !== 'all'
            ? Number(selectedUserRef)
            : null;

      let operatorLabel: string | undefined;
      if (resolvedUserRef != null) {
        const fromReport = report.items.find((x) => x.user_ref === resolvedUserRef);
        if (fromReport) {
          operatorLabel = fromReport.operator_name || fromReport.username;
        } else {
          const fromOps = operators.find((x) => x.user_id === resolvedUserRef);
          operatorLabel = fromOps?.full_name || fromOps?.username;
        }
      } else {
        operatorLabel = 'همه کاربران';
      }

      setDetailOperatorLabel(operatorLabel);
      setDetailOpen(true);
      setDetailLoading(true);
      setDetailError(null);
      setDetailData(null);

      try {
        const params = new URLSearchParams({
          kind,
          from_date: report.from_date,
          to_date: report.to_date,
        });
        if (resolvedUserRef != null) params.set('user_ref', String(resolvedUserRef));
        if (report.machine_ref != null) params.set('machine_ref', String(report.machine_ref));

        const apiBaseUrl = getApiBaseUrl();
        const res = await fetch(
          `${apiBaseUrl}/admin/reports/his/operator-details?${params}`,
          { headers: authHeaders() }
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || 'خطا در دریافت لیست جزئیات');
        }
        setDetailData(await res.json());
      } catch (err: unknown) {
        setDetailError(err instanceof Error ? err.message : 'خطای ناشناخته');
      } finally {
        setDetailLoading(false);
      }
    },
    [report, selectedUserRef, operators, authHeaders]
  );

  const exportCsv = () => {
    if (!report?.items.length) return;
    const showCash = report.payment_type !== 'insurance';
    const showInsurance = report.payment_type !== 'cash';
    const exportCountLabel = report.machine_ref ? 'تعداد خدمت' : 'تعداد پذیرش';
    const cols = ['رتبه', 'کاربر', 'نام کاربری', exportCountLabel, 'نوبت‌دهی', 'انجام کار'];
    if (showCash) cols.push('نقدی');
    if (showInsurance) cols.push('بیمه');
    if (report.payment_type === 'all') cols.push('جمع');

    const rows = report.items.map((item, i) => {
      const row: (string | number)[] = [
        i + 1,
        `"${item.operator_name.replace(/"/g, '""')}"`,
        item.username,
        item.admitted_timing_count ?? 0,
        item.timing_count ?? 0,
        item.dowork_count ?? 0,
      ];
      if (showCash) row.push(item.cash_amount);
      if (showInsurance) row.push(item.insurance_amount);
      if (report.payment_type === 'all') row.push(item.total_amount);
      return row.join(',');
    });

    const blob = new Blob(['\ufeff' + cols.join(',') + '\n' + rows.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `his-users-${report.from_date}-${report.to_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const displayTotal = report
    ? report.payment_type === 'cash'
      ? report.total_cash
      : report.payment_type === 'insurance'
        ? report.total_insurance
        : report.total_amount
    : 0;

  const isSingleUser = selectedUserRef !== 'all';
  const singleItem = report?.items[0] ?? null;
  const loadingOverlayOpen = loadingReport || loadingOperators;
  const loadingTitle = loadingReport ? 'در حال بارگذاری گزارش' : 'در حال بارگذاری کاربران';
  const loadingSubtitle = loadingReport
    ? 'دریافت آمار مالی کاربران'
    : 'دریافت لیست کاربران فعال';

  const countLabel = report?.machine_ref ? 'تعداد خدمت' : 'تعداد پذیرش';
  const receptionTip = report?.machine_ref
    ? CARD_TIPS.receptionService
    : CARD_TIPS.reception;

  const connectionBadge = connection ? (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
        connection.ok
          ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/80'
          : connection.configured
            ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200/80'
            : 'bg-rose-50 text-rose-700 ring-1 ring-rose-200/80'
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          connection.ok ? 'bg-emerald-500' : connection.configured ? 'bg-amber-500' : 'bg-rose-500'
        }`}
      />
      {connection.configured
        ? connection.ok
          ? 'متصل'
          : 'مشکل اتصال'
        : 'اتصال برقرار نیست'}
    </span>
  ) : null;

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-l from-indigo-600 via-indigo-700 to-slate-800 px-5 py-4 text-white shadow-lg shadow-indigo-900/20 sm:px-6">
        <div
          className="pointer-events-none absolute -left-16 -top-16 h-48 w-48 rounded-full bg-white/10 blur-2xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-12 -right-8 h-40 w-40 rounded-full bg-indigo-400/20 blur-2xl"
          aria-hidden
        />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-indigo-200">
              گزارش مالی
            </p>
            <h1 className="mt-0.5 text-xl font-bold tracking-tight sm:text-2xl">{HIS_MENU_TITLE}</h1>
            <p className="mt-1 hidden max-w-md text-xs text-indigo-100/90 sm:block">
              از نوبت‌دهی تا پذیرش و انجام کار — به تفکیک کاربر
            </p>
          </div>
          {connectionBadge}
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-slate-200/80 bg-white p-2 shadow-sm sm:p-2.5">
        <div className="flex flex-col gap-2 xl:flex-row xl:items-center">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <div className="flex gap-0.5 rounded-lg bg-slate-100 p-0.5">
              {DATE_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyDatePreset(p.id)}
                  className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                    datePreset === p.id
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="flex items-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50/60">
              <div className="w-[7.25rem] border-l border-slate-200">
                <PersianDatePicker
                  compact
                  borderless
                  value={fromDate}
                  placeholder="از"
                  onChange={(v) => {
                    setFromDate(v);
                    setDatePreset('custom');
                    setOperatorsLoaded(false);
                    setSelectedUserRef('');
                    setReport(null);
                  }}
                />
              </div>
              <div className="w-[7.25rem]">
                <PersianDatePicker
                  compact
                  borderless
                  value={toDate}
                  placeholder="تا"
                  onChange={(v) => {
                    setToDate(v);
                    setDatePreset('custom');
                    setOperatorsLoaded(false);
                    setSelectedUserRef('');
                    setReport(null);
                  }}
                />
              </div>
            </div>

            <button
              type="button"
              onClick={() => void loadOperators()}
              disabled={loadingOperators}
              title="بارگذاری کاربران"
              className="relative inline-flex h-8 shrink-0 items-center gap-1 rounded-lg bg-slate-800 px-2.5 text-[11px] font-medium text-white transition hover:bg-slate-700 disabled:opacity-50"
            >
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              بارگذاری
              {operatorsLoaded ? (
                <span className="rounded bg-white/20 px-1 py-px text-[10px] tabular-nums">
                  {operators.length.toLocaleString('fa-IR')}
                </span>
              ) : null}
            </button>
          </div>

          <div className="hidden h-7 w-px shrink-0 bg-slate-200 xl:block" />

          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              <select
                value={selectedUserRef}
                onChange={(e) => {
                  setSelectedUserRef(e.target.value);
                  setReport(null);
                }}
                disabled={!operatorsLoaded}
                className={`select-rtl select-rtl-compact h-8 min-w-0 flex-1 truncate rounded-lg border border-slate-200 bg-slate-50/50 py-0 pr-2.5 text-xs text-slate-800 focus:border-indigo-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:cursor-not-allowed ${
                  operatorsLoaded ? '' : 'pointer-events-none opacity-50'
                }`}
              >
                <option value="">کاربر...</option>
                <option value="all">همه ({operators.length.toLocaleString('fa-IR')})</option>
                {operators.map((op) => (
                  <option key={op.user_id} value={String(op.user_id)}>
                    {op.full_name || op.username}
                    {op.username ? ` · ${op.username}` : ''}
                  </option>
                ))}
              </select>

              <select
                value={selectedMachineRef}
                onChange={(e) => {
                  setSelectedMachineRef(e.target.value);
                  setReport(null);
                }}
                disabled={!operatorsLoaded || machines.length === 0}
                className={`select-rtl select-rtl-compact h-8 min-w-0 flex-1 truncate rounded-lg border border-slate-200 bg-slate-50/50 py-0 pr-2.5 text-xs text-slate-800 focus:border-indigo-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:cursor-not-allowed ${
                  operatorsLoaded && machines.length > 0 ? '' : 'pointer-events-none opacity-50'
                }`}
              >
                <option value="all">همه خدمات</option>
                {machines.map((machine) => (
                  <option key={machine.machine_id} value={String(machine.machine_id)}>
                    {machine.display_name}
                    {machine.display_name !== machine.machine_name
                      ? ` · ${machine.machine_name}`
                      : ''}
                  </option>
                ))}
              </select>
            </div>

            <div
              className={`inline-flex shrink-0 rounded-lg bg-slate-100 p-0.5 ${selectedUserRef ? '' : 'pointer-events-none opacity-50'}`}
            >
              {PAYMENT_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  disabled={!selectedUserRef}
                  title={opt.desc}
                  onClick={() => {
                    setPaymentType(opt.id);
                    setReport(null);
                  }}
                  className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition-all ${
                    paymentType === opt.id
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => void fetchReport()}
              disabled={!selectedUserRef || loadingReport}
              className="inline-flex h-8 shrink-0 items-center gap-1 rounded-lg bg-indigo-600 px-3 text-[11px] font-semibold text-white transition hover:bg-indigo-700 disabled:opacity-50 xl:mr-auto"
            >
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              اجرا
            </button>
            {report?.items.length ? (
              <button
                type="button"
                onClick={exportCsv}
                title="خروجی CSV"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <ReportLoadingOverlay
        open={loadingOverlayOpen}
        title={loadingTitle}
        subtitle={loadingSubtitle}
      />

      <DetailListModal
        open={detailOpen}
        loading={detailLoading}
        error={detailError}
        data={detailData}
        operatorLabel={detailOperatorLabel}
        onClose={closeDetail}
      />

      {error && (
        <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <svg className="mt-0.5 h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}

      {/* Results */}
      {report && (
        <div className="space-y-5">
          {(() => {
            const counts = isSingleUser && singleItem
              ? {
                  reception: singleItem.admitted_timing_count ?? 0,
                  timing: singleItem.timing_count ?? 0,
                  cashSwipe: singleItem.cash_count ?? 0,
                  dowork: singleItem.dowork_count ?? 0,
                  cashAmount: singleItem.cash_amount,
                  insuranceAmount: singleItem.insurance_amount,
                  totalAmount: singleItem.total_amount,
                }
              : {
                  reception: report.total_admitted_timings ?? 0,
                  timing: report.total_timings ?? 0,
                  cashSwipe: report.total_cash_swipes ?? 0,
                  dowork: report.total_doworks ?? 0,
                  cashAmount: report.total_cash,
                  insuranceAmount: report.total_insurance,
                  totalAmount: displayTotal,
                };

            return (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <StatCard
                    label="تعداد پذیرش"
                    value={counts.reception.toLocaleString('fa-IR')}
                    tip="تعداد نوبت‌هایی که در بازه پذیرش هم شده‌اند."
                    accent="sky"
                    icon={ICONS.clipboard}
                    onClick={() => void openDetail('reception')}
                  />
                  <StatCard
                    label="نوبت‌دهی"
                    value={counts.timing.toLocaleString('fa-IR')}
                    tip={CARD_TIPS.timing}
                    accent="amber"
                    icon={ICONS.calendar}
                    onClick={() => void openDetail('timing')}
                  />
                  <StatCard
                    label="انجام کار"
                    value={counts.dowork.toLocaleString('fa-IR')}
                    tip={CARD_TIPS.dowork}
                    accent="teal"
                    icon={ICONS.check}
                    onClick={() => void openDetail('dowork_from_timing')}
                  />
                </div>
                <div
                  className={`grid gap-3 ${
                    report.payment_type === 'all'
                      ? 'grid-cols-1 sm:grid-cols-3'
                      : 'grid-cols-1 sm:grid-cols-2'
                  }`}
                >
                  {report.payment_type !== 'insurance' && (
                    <StatCard
                      label={report.payment_type === 'cash' ? `جمع ${report.payment_type_label}` : 'نقدی'}
                      {...rialMoneyStat(counts.cashAmount)}
                      tip={CARD_TIPS.cashAmount}
                      accent="emerald"
                      icon={ICONS.currency}
                    />
                  )}
                  {report.payment_type !== 'cash' && (
                    <StatCard
                      label={report.payment_type === 'insurance' ? `جمع ${report.payment_type_label}` : 'بیمه'}
                      {...rialMoneyStat(counts.insuranceAmount)}
                      tip={CARD_TIPS.insuranceAmount}
                      accent="violet"
                      icon={ICONS.currency}
                    />
                  )}
                  {report.payment_type === 'all' && (
                    <StatCard
                      label={`جمع ${report.payment_type_label}`}
                      {...rialMoneyStat(counts.totalAmount)}
                      tip={CARD_TIPS.totalAmount}
                      accent="indigo"
                      icon={ICONS.currency}
                    />
                  )}
                </div>
              </div>
            );
          })()}

          {!isSingleUser && report.items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-14 text-center">
              <p className="text-sm font-medium text-slate-600">داده‌ای یافت نشد</p>
              <p className="mt-1 text-xs text-slate-400">فیلتر یا بازه را تغییر دهید</p>
            </div>
          ) : !isSingleUser && report.items.length > 0 ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">کاربران</h3>
                  <p className="text-xs text-slate-500">مرتب‌شده بر اساس بیشترین مبلغ</p>
                </div>
                <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700">
                  {report.items.length.toLocaleString('fa-IR')} کاربر
                </span>
              </div>
              <div className="space-y-6">
                {report.items.map((item, index) => (
                  <UserStatBlock
                    key={item.user_ref}
                    rank={index + 1}
                    item={item}
                    paymentType={report.payment_type}
                    countLabel={countLabel}
                    receptionTip={receptionTip}
                    onOpenDetail={(kind, userRef) => void openDetail(kind, userRef)}
                  />
                ))}
              </div>
            </div>
          ) : isSingleUser && !singleItem ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-14 text-center">
              <p className="text-sm font-medium text-slate-600">داده‌ای یافت نشد</p>
              <p className="mt-1 text-xs text-slate-400">فیلتر یا بازه را تغییر دهید</p>
            </div>
          ) : null}
        </div>
      )}

      {/* Empty state before first report */}
      {!report && !error && !loadingReport && (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-14 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-600">
            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-slate-700">گزارشی هنوز اجرا نشده</p>
          <p className="mx-auto mt-1 max-w-sm text-xs text-slate-500">
            بازه زمانی را مشخص کنید، کاربران را بارگذاری کنید و دکمه «اجرای گزارش» را بزنید
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminHisReports;
